<script setup lang="ts">
import { required as veeRequiredValidator } from '@vee-validate/rules'
import { defineRule, Field as VeeField } from 'vee-validate'

import { useAddressesStore } from '@/store/addresses/addresses'
import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'
import { useToggleStore } from '@/store/toggle/toggle'

import { AddressClient } from '@/api/modules/addresses/addresses.types'
import { DeliveryMethods } from '@/api/modules/basket/types'
import { Success } from '@/api/results'

import AppButton from '@/components/App/AppButton.vue'
import BasketAddressList from '@/components/Basket/basket-addresses/BasketAddressList.vue'
import BasketDeliveryAddressItem from '@/components/Basket/basket-addresses/BasketDeliveryAddressItem.vue'

const AppProgress = defineAsyncComponent(
  () => import('~/components/App/AppProgress.vue'),
)
const BasketFailed = defineAsyncComponent(
  () => import('~/components/Basket/basket-utils/BasketFailed.vue'),
)
const DialogBasketAddAddressForm = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketAddAddressForm.vue'),
)
const NewDialogBasketAddAddressForm = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/NewDialogBasketAddAddressForm.vue'),
)
const NewDialogBasketEditAddress = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/NewDialogBasketEditAddress.vue'),
)
const NewDialogBasketDeleteAddress = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/NewDialogBasketDeleteAddress.vue'),
)
const DialogBasketEditAddressForm = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketEditAddressForm.vue'),
)
const DialogBasketDeleteAddress = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketDeleteAddress.vue'),
)
const DialogRedirectOtherCity = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogRedirectOtherCity.vue'),
)

const BasketDeliveryAddressNotification = defineAsyncComponent(
  () =>
    import(
      '~/components/Basket/basket-addresses/BasketDeliveryAddressNotification.vue'
    ),
)

const { showSnackbar } = useAppStore()
const basketStore = useBasketStore()
const addressesStore = useAddressesStore()
const { newFetchingApiEnabled } = useToggleStore()
const { cityInfo } = useCityStore()

const emit = defineEmits(['deliveryMethodChanged', 'selectDeliveryCourier'])

const { isMobile, isMobileOrTablet } = useDevice()

const dialogs = ref({
  addAddress: {
    visible: false,
  },
  editAddress: {
    visible: false,
    editableAddress: null as AddressClient | null,
  },
  deleteAddress: {
    visible: false,
    address: null as AddressClient | null,
  },
  redirectCity: {
    visible: false,
    address: null as AddressClient | null,
  },
  isShowNotification: false,
})

const basket = computed(() => basketStore.data)

const addresses = computed(() => addressesStore.addresses)

const deliveryCost = computed(() => basket.value.delivery.cost)

const selectedAddress = computed(() => {
  const settedAddress =
    basket.value.delivery.addresses[DeliveryMethods.COURIER] || null

  // Ищем целиком адрес для передачи в v-radio-group
  const searchAddress = addresses.value.data.find(
    (address: AddressClient) => address.id === settedAddress,
  )

  return searchAddress || null
})

const setSelectedAddress = async (value: AddressClient) => {
  if (!value || ('is_in_delivery_area' in value && !value.is_in_delivery_area))
    return

  await basketStore.setDeliveryMethod({
    address_id: value.id,
    delivery_method: DeliveryMethods.COURIER,
  })

  emit('deliveryMethodChanged')
}

const openAddAddressDialog = () => {
  dialogs.value.addAddress.visible = true
}

const openEditAddressDialog = (address: AddressClient) => {
  dialogs.value.editAddress.visible = true
  dialogs.value.editAddress.editableAddress = address
}

const closeEditAddressDialog = () => {
  dialogs.value.editAddress.visible = false
  dialogs.value.editAddress.editableAddress = null
}

const openDeleteAddressDialog = (address: AddressClient) => {
  dialogs.value.deleteAddress.visible = true
  dialogs.value.deleteAddress.address = address
}

const openDialogRedirectCity = (address: AddressClient) => {
  dialogs.value.redirectCity.visible = true
  dialogs.value.redirectCity.address = address
}

const openDialogNotification = () => {
  dialogs.value.isShowNotification = true
}

const closeDialogNotification = () => {
  dialogs.value.isShowNotification = false
}

const closeDialogRedirectCity = () => {
  dialogs.value.redirectCity.visible = false
}

const selectDeliveryCourier = () => {
  emit('selectDeliveryCourier')
}

const addAddress = async (address: AddressClient) => {
  const { city_domain_prefix, is_in_delivery_area, id } = address

  // Если адрес в зоне доставки, то не устанавливаем его
  if (is_in_delivery_area && !newFetchingApiEnabled) {
    await basketStore.setDeliveryMethod({
      delivery_method: DeliveryMethods.COURIER,
      address_id: id,
    })
  }

  // В случае если адрес в зоне доставки и адрес текущего города
  const isAllowedAddress =
    is_in_delivery_area &&
    newFetchingApiEnabled &&
    city_domain_prefix === cityInfo.domain_prefix

  if (isAllowedAddress) {
    await basketStore.setDeliveryMethod({
      delivery_method: DeliveryMethods.COURIER,
      address_id: id,
    })
  }

  emit('deliveryMethodChanged')

  dialogs.value.addAddress.visible = false

  await fetchAddress()

  showSnackbar({
    message: 'Адрес добавлен',
    type: SnackbarType.SUCCESS,
  })

  // Если адрес вне зоны доставки
  if (!is_in_delivery_area) {
    openDialogNotification()

    return
  }

  // Если адрес в зоне доставки и другой город
  if (newFetchingApiEnabled && city_domain_prefix !== cityInfo.domain_prefix) {
    openDialogRedirectCity(address)

    return
  }

  basketStore.setAddress({
    type: DeliveryMethods.COURIER,
    id,
  })
}

const fetchAddress = async () => {
  const result = await addressesStore.getAddresses()

  if (result instanceof Success) return

  showSnackbar({ message: 'Не удалось загрузить список адресов' })
}

const editAddress = async () => {
  dialogs.value.editAddress.visible = false

  await fetchAddress()
}

const closeDialogDeleteAddress = async (
  isUpdate: boolean,
  addressId?: number,
) => {
  dialogs.value.deleteAddress.visible = false

  // В случае, если адреса не нужно обновлять или нет id
  if (!isUpdate || !addressId) return

  await fetchAddress()

  if (basket.value.delivery.addresses.courier === addressId) {
    await basketStore.fetchBasket()
  }
}

const delAddress = async (addressId: number) => {
  dialogs.value.editAddress.visible = false

  await fetchAddress()

  if (basket.value.delivery.addresses.courier === addressId) {
    await basketStore.fetchBasket()
  }
}

const requiredAddress = (value: string) => {
  if (!veeRequiredValidator(value)) {
    return 'Выберите адрес'
  }

  return true
}

defineRule('required-address', requiredAddress)
</script>

<template>
  <div id="address_id" class="courier">
    <app-progress v-if="addresses.isLoading" :size="100" />
    <basket-failed
      v-else-if="addresses.error"
      :error-message="'Не удалось загрузить список адресов'"
      @update="fetchAddress"
    />
    <div v-else>
      <div v-if="addresses.data.length === 0" class="address-message">
        Укажите адрес для доставки
      </div>
      <vee-field
        v-slot="{ errors, field }"
        rules="required-address"
        name="address_id"
        :model-value="selectedAddress"
        validate-on-mount
      >
        <basket-address-list
          v-if="addresses.data.length"
          v-bind="field"
          :model-value="selectedAddress || undefined"
          class="address-list"
          :max-count-of-visible-addresses="5"
          :addresses="addresses.data"
          :delivery-cost="deliveryCost"
          @delivery-method-changed="emit('deliveryMethodChanged')"
          @select-delivery-courier="emit('selectDeliveryCourier')"
          @open-edit-address-dialog="openEditAddressDialog"
          @open-delete-address-dialog="openDeleteAddressDialog"
          @open-dialog-notification="openDialogNotification"
          @open-dialog-redirect-city="openDialogRedirectCity"
          @update:model-value="setSelectedAddress"
        >
          <template #default="{ address }">
            <basket-delivery-address-item
              :delivery-cost="
                address.id === selectedAddress?.id ? deliveryCost : null
              "
              :address="address"
            />
          </template>
        </basket-address-list>
        <app-button
          :ripple="false"
          class="add-address"
          variant="text"
          @click="openAddAddressDialog"
        >
          <nuxt-img class="icon" src="/add-plus.svg" width="24" height="24" />
          Добавить адрес
        </app-button>
        <div v-if="Object.keys(errors).length" class="errors">
          <nuxt-img
            src="/basket-failed-error.svg"
            alt="attention"
            width="20"
            height="20"
          />
          {{ errors[0] }}
        </div>
        <div
          v-else-if="deliveryCost && deliveryCost.error_message"
          class="errors"
        >
          <nuxt-img
            src="/basket-failed-error.svg"
            alt="attention"
            width="20"
            height="20"
          />
          {{ deliveryCost.error_message }}
        </div>
        <div
          v-else-if="deliveryCost && deliveryCost.help_message"
          class="messages"
        >
          <nuxt-img src="/winking-face.png" alt="winking face" />
          {{ deliveryCost.help_message }}
        </div>
      </vee-field>
    </div>

    <app-dialog
      v-model="dialogs.editAddress.visible"
      :fullscreen="isMobile"
      :width="!isMobile ? 560 : ''"
    >
      <template #title>
        <div v-if="isMobileOrTablet" class="edit-address-title">
          Редактирование адреса
        </div>
      </template>

      <template v-if="dialogs.editAddress.editableAddress" #body>
        <transition v-if="!newFetchingApiEnabled" :duration="150">
          <dialog-basket-edit-address-form
            :address="dialogs.editAddress.editableAddress"
            @delete="delAddress"
            @edit="editAddress"
          />
        </transition>

        <transition v-if="newFetchingApiEnabled" :duration="150">
          <new-dialog-basket-edit-address
            :address="dialogs.editAddress.editableAddress"
            @delete="delAddress"
            @edit="editAddress"
            @close="closeEditAddressDialog"
          />
        </transition>
      </template>
    </app-dialog>

    <app-dialog
      v-model="dialogs.addAddress.visible"
      :fullscreen="isMobile"
      :width="!isMobile ? 560 : ''"
    >
      <template #title>
        <div v-if="isMobileOrTablet" class="add-address-title">
          Добавление адреса
        </div>
      </template>

      <template #body>
        <new-dialog-basket-add-address-form
          v-if="newFetchingApiEnabled"
          @add="addAddress"
        />

        <dialog-basket-add-address-form v-else @add="addAddress" />
      </template>
    </app-dialog>

    <app-dialog v-model="dialogs.deleteAddress.visible" width="360">
      <template v-if="dialogs.deleteAddress.address" #body>
        <new-dialog-basket-delete-address
          v-if="newFetchingApiEnabled"
          :address="dialogs.deleteAddress.address"
          @close-dialog-delete-address="closeDialogDeleteAddress"
        />

        <dialog-basket-delete-address
          v-else
          :address="dialogs.deleteAddress.address"
          @close-dialog-delete-address="closeDialogDeleteAddress"
        />
      </template>
    </app-dialog>

    <app-dialog
      v-if="newFetchingApiEnabled"
      v-model="dialogs.redirectCity.visible"
      max-width="620"
    >
      <template v-if="dialogs.redirectCity.address" #body>
        <dialog-redirect-other-city
          :address="dialogs.redirectCity.address"
          @close-dialog-delete-address="closeDialogRedirectCity"
          @close-dialog-redirect-city="closeDialogRedirectCity"
        />
      </template>
    </app-dialog>

    <v-dialog
      v-if="dialogs.isShowNotification"
      ref="dialog"
      :model-value="dialogs.isShowNotification"
      max-width="620"
      v-bind="$attrs"
      @keydown.esc="closeDialogNotification"
      @update:model-value="closeDialogNotification"
      @close="closeDialogNotification"
    >
      <basket-delivery-address-notification
        @select-delivery-courier="selectDeliveryCourier"
        @close="closeDialogNotification"
      />
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.add-address-title,
.edit-address-title {
  font-size: 20px;
  font-weight: 800;
}
.messages {
  display: flex;
  margin-top: 33px;
  font-size: 14px;
  font-weight: 600;
  img {
    align-self: flex-start;
    width: 24px;
    margin-right: 12px;
  }
}
.errors {
  display: flex;
  margin-top: 33px;
  font-size: 14px;
  font-weight: 600;
  color: $danger;
  img {
    align-self: flex-start;
    width: 24px;
    margin-right: 12px;
  }
}
.address-list {
  margin-bottom: 10px;
}
.address-message {
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: 800;
}
.add-address {
  display: block;
  min-height: unset !important;
  padding: unset !important;
  font-weight: 700;

  .icon {
    margin-right: 16px;
  }
}

:deep() {
  .v-radio--is-disabled {
    .v-input--selection-controls__input span path:nth-child(2) {
      fill: #949498 !important;
    }
    .address {
      color: #949498 !important;
    }
  }
  .v-dialog {
    overflow-y: inherit;
    border-radius: 16px !important;
  }
}
</style>
